import { faInfoCircle, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { IconButton } from 'atoms/buttons/IconButton';
import { FontAwesomeIcon } from 'atoms/icons';
import { Horizontal } from 'atoms/layout/flex';
import { LabelXS } from 'atoms/typography';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useWalletNeedsRecover } from 'hooks/recovery/useWalletNeedsRecover';
import { LIFECYCLE, useLifecycle } from 'hooks/useLifecycle';
import { useOAuthEthWallet } from 'hooks/wallets/useOAuthEthWallet';
import { glossary } from 'lib/glossary';

import { RedDot } from './RedDot';

const Root = styled(Horizontal)`
  position: relative;
  padding: var(--unit);
`;
const NewChip = styled.div`
  color: #00f3eb;
  background: rgba(0, 243, 235, 0.16);
  border-radius: var(--double-unit);
  padding: 2px var(--unit);
`;
const StyledRedDot = styled(RedDot)`
  position: absolute;
  top: var(--unit);
  right: var(--unit);
`;

type Props = {
  onClick?: () => void;
  sidebarCollapsed?: boolean;
};
export const Deposit = ({ onClick: onClickProp, sidebarCollapsed }: Props) => {
  const isLaptop = useIsLaptop();
  const { lifecycle, update: updateLifecycle } = useLifecycle();

  const onClick = () => {
    updateLifecycle(LIFECYCLE.sawDepositButton, true);
    onClickProp?.();
  };

  const { currentUser } = useCurrentUserContext();
  const walletNeedsRecover = useWalletNeedsRecover();
  const { needsCreateEthWallet } = useOAuthEthWallet();
  const needConfirmedDevice = !currentUser?.confirmedDevice;

  const showWarning =
    !!walletNeedsRecover || !!needConfirmedDevice || !!needsCreateEthWallet;

  const sawDepositButton = lifecycle?.[LIFECYCLE.sawDepositButton];

  const showNewChip = !sawDepositButton && !showWarning;

  if (sidebarCollapsed) {
    return (
      <Root>
        {showWarning ? (
          <IconButton icon={faInfoCircle} color="red" size="compact" />
        ) : (
          <IconButton
            color="quaternary"
            size="compact"
            icon={faPlus}
            onClick={onClick}
          />
        )}
        {!!showNewChip && <StyledRedDot />}
      </Root>
    );
  }

  return (
    <Root>
      <Button
        fullWidth
        color={showWarning ? 'red' : 'quaternary'}
        size={isLaptop ? 'compact' : 'small'}
        startIcon={
          <FontAwesomeIcon icon={showWarning ? faInfoCircle : faPlus} />
        }
        onClick={onClick}
      >
        <Horizontal>
          {showWarning ? (
            <FormattedMessage
              id="Deposit.cta.actionRequired"
              defaultMessage="Action required"
            />
          ) : (
            <FormattedMessage id="Deposit.cta.text" defaultMessage="Deposit" />
          )}
          {!!showNewChip && (
            <NewChip>
              <LabelXS bold>
                <FormattedMessage {...glossary.new} />
              </LabelXS>
            </NewChip>
          )}
        </Horizontal>
      </Button>
    </Root>
  );
};
